import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios, { AxiosError } from 'axios';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from 'commons/constants';
import i18next from 'i18next';
import produce from 'immer';
import { uploadFiles } from 'services/uploader';
import { createMember } from 'services/users';
import { storeName as entityStore } from 'store/entities/users.entity';
import { PageRequest } from 'types/common.d';
import { UserType } from 'types/entities/users.d';
import { UsersModel } from 'types/models/users.d';
import { PaginationType } from 'types/response.d';
import { convertFilesUploadDataToDto } from 'utils/file';
import { RootModel } from '../models';
import { storeName as userModel } from './user.model';

export const storeName = 'usersModel';

const initialState: UsersModel = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: {
    keyword: '',
  },
  permissions: {
    content: [],
    pagination: DEFAULT_PAGINATION,
  },
  editUserModalType: null,
};

const setAvatar = (data, user, uploadActions: Promise<void>[]) => {
  if (data.avatar) {
    const promise = uploadFiles(
      data.code,
      data.avatar,
      FILE_NAME_TYPE.AVATAR,
      user.id,
      'userContact'
    ).then((result) => {
      convertFilesUploadDataToDto(data, 'avatar', result, false);
    });
    uploadActions.push(promise);
  }
};

export const usersModel = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setActionAdminChangePassword: produce((state, payload) => {
      state.adminChangePassword = payload;
    }),
    setEditUserModalType: produce((state, payload) => {
      state.editUserModalType = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof usersModel>) => ({
    async setEditItem(payload) {
      this.setEditId(payload.editId);
      this.setEditUserModalType(payload.editUserModalType);
    },
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async filterAsync(payload) {
      this.updateLoading(true);
      try {
        const data: { content: UserType[] } & PaginationType = await dispatch[entityStore].load(
          payload
        );
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async getAsync(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async publishAsync(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].publish(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const { user } = state[userModel];
        const data = payload;
        const uploadActions: Promise<void>[] = [];
        setAvatar(data, user, uploadActions);
        if (data.translations && !!data.translations.length) {
          data.translations.forEach((translation) => {
            setAvatar(translation, user, uploadActions);
          });
        }
        await axios.all(uploadActions);
        await dispatch[entityStore].save(data);
        message.success('Save successfully');
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
        throw err;
      } finally {
        this.updateLoading(false);
      }
    },

    async createNew(payload, state) {
      this.updateLoading(true);
      try {
        const res = await createMember(payload);

        if (res && res.data) {
          message.success('Save successfully');
          return res.data;
        }
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          const { data } = error.response;
          const { message: errorMessage } = data;
          if (errorMessage !== '') {
            message.error(errorMessage);
          } else {
            message.error('Failed to register user.');
          }
        }
      } finally {
        this.updateLoading(false);
      }
    },

    async adminChangePassword(payload) {
      this.updateLoading(true);
      try {
        await dispatch[entityStore].forceChangePassword(payload);
        message.success(
          i18next.t('users-management.admin-change-password.msg-change-password-success')
        );
      } catch (err) {
        console.log(err);
        const msgErr = err.message;

        if (msgErr !== '') {
          message.error(msgErr);
        } else {
          message.error(
            i18next.t('users-management.admin-change-password.error.msg-change-password-error')
          );
        }
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});

export default usersModel;
